import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Paper, Typography, Button, CircularProgress, Alert, Stack, Chip,
} from '@mui/material';
import { Print, PictureAsPdf, GridOn, Refresh, CheckCircle, ErrorOutline } from '@mui/icons-material';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { reportTrialBalanceURL } from '../../BaseURL';

function headers() { return { Authorization: `Bearer ${localStorage.getItem('church_token')}` }; }

const fmt = (n) =>
  Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDateLong = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-GH', { day: '2-digit', month: 'long', year: 'numeric' });
};

const NAVY  = '#0B2545';
const GREEN = '#0F766E';
const RED   = '#B91C1C';
const RULE  = '#0B2545';

export default function TrialBalance({ start, end }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const printRef = useRef(null);

  const load = () => {
    if (!start || !end) return;
    setLoading(true); setError('');
    axios.get(reportTrialBalanceURL, {
      headers: headers(),
      params: { start, end },
    })
      .then(r => { if (r.data.success) setData(r.data); else setError(r.data.message); })
      .catch(err => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);

  // Flatten to rows for rendering / export: { name, section, debit, credit, indent }
  const rows = useMemo(() => {
    if (!data) return [];
    const out = [];
    // Assets
    out.push({ type: 'section', label: 'ASSETS' });
    data.assets.banks.forEach(b => {
      out.push({ type: 'item', name: b.name, debit: b.closing > 0 ? b.closing : 0, credit: b.closing < 0 ? -b.closing : 0 });
    });
    out.push({
      type: 'item', name: 'Cash in Hand',
      debit:  data.assets.cash_in_hand.closing > 0 ? data.assets.cash_in_hand.closing : 0,
      credit: data.assets.cash_in_hand.closing < 0 ? -data.assets.cash_in_hand.closing : 0,
    });
    out.push({ type: 'subtotal', name: 'Total Assets', debit: data.assets.total_closing, credit: 0 });

    // Income
    out.push({ type: 'section', label: 'INCOME' });
    if (data.income.items.length === 0) {
      out.push({ type: 'note', name: 'No income for this period' });
    } else {
      data.income.items.forEach(it => {
        out.push({ type: 'item', name: `${it.name} (${it.category})`, debit: 0, credit: Number(it.amount) });
      });
      out.push({ type: 'subtotal', name: 'Total Income', debit: 0, credit: data.income.total });
    }

    // Expenses
    out.push({ type: 'section', label: 'EXPENSES' });
    if (data.expenses.items.length === 0) {
      out.push({ type: 'note', name: 'No expenses for this period' });
    } else {
      data.expenses.items.forEach(it => {
        out.push({ type: 'item', name: `${it.name} (${it.category})`, debit: Number(it.amount), credit: 0 });
      });
      out.push({ type: 'subtotal', name: 'Total Expenses', debit: data.expenses.total, credit: 0 });
    }

    // Opening equity (balancing figure)
    out.push({ type: 'section', label: 'CAPITAL & RESERVES' });
    out.push({ type: 'item', name: 'Opening Balance b/f', debit: 0, credit: data.opening_equity });

    return out;
  }, [data]);

  // ─── PRINT ────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    if (!printRef.current) return;
    const w = window.open('', '_blank', 'width=1080,height=820');
    if (!w) return;
    w.document.write(`
      <html><head><title>Trial Balance - ${data?.period.label || ''}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: 'Helvetica Neue', -apple-system, Segoe UI, Roboto, Arial, sans-serif; margin: 28px 32px; color: #111; }
        .letterhead { display: flex; align-items: center; gap: 20px; padding-bottom: 14px; border-bottom: 3px double ${NAVY}; }
        .letterhead img { width: 84px; height: 84px; object-fit: contain; border-radius: 6px; }
        .letterhead .info h1 { margin: 0; font-size: 26px; letter-spacing: 0.02em; color: ${NAVY}; }
        .letterhead .info p { margin: 3px 0 0; color: #555; font-size: 12px; }
        .stmt-title { text-align: center; margin: 22px 0 4px; }
        .stmt-title .name { font-size: 15px; font-weight: 800; letter-spacing: 0.16em; color: ${NAVY}; border-top: 1.5px solid ${NAVY}; border-bottom: 1.5px solid ${NAVY}; display: inline-block; padding: 4px 24px; }
        .stmt-title .period { margin-top: 8px; font-size: 12px; color: #555; }
        table { width: 100%; border-collapse: collapse; margin-top: 18px; }
        thead th { text-align: left; font-size: 10.5px; letter-spacing: 0.08em; text-transform: uppercase; color: #fff; background: ${NAVY}; padding: 9px 12px; }
        thead th.right { text-align: right; }
        tbody td { padding: 6px 12px; font-size: 12.5px; border-bottom: 1px solid #eef0f4; }
        tbody td.right { text-align: right; font-variant-numeric: tabular-nums; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        .section td { background: ${NAVY}; color: #fff; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; padding: 8px 12px; font-size: 11.5px; }
        .item td { padding-left: 24px; }
        .subtotal td { border-top: 1px solid ${NAVY}; border-bottom: 1px solid ${NAVY}; font-weight: 800; color: ${NAVY}; background: #eef2f7; text-transform: uppercase; font-size: 12px; }
        .grand td { border-top: 3px double ${NAVY}; border-bottom: 3px double ${NAVY}; font-weight: 800; font-size: 14px; background: #fffdf5; padding: 12px; text-transform: uppercase; letter-spacing: 0.1em; color: ${NAVY}; }
        .balance-check { text-align: center; margin-top: 16px; padding: 10px; border-radius: 6px; font-weight: 700; }
        .balance-check.ok { background: #e7f5ef; color: ${GREEN}; border: 1px solid ${GREEN}; }
        .balance-check.bad { background: #fdecec; color: ${RED}; border: 1px solid ${RED}; }
        .footer { display: flex; justify-content: space-between; margin-top: 30px; font-size: 10px; color: #888; }
        @page { size: A4; margin: 15mm; }
      </style>
      </head><body>${printRef.current.innerHTML}</body></html>
    `);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 300);
  };

  // ─── EXPORT PDF ───────────────────────────────────────────────────────────
  const handlePdf = () => {
    if (!data) return;
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 42;

    // Letterhead
    let y = 40;
    const logoSize = 60;
    let textLeft = margin;
    if (data.church?.logo && String(data.church.logo).startsWith('data:image')) {
      try {
        const fmtType = data.church.logo.substring(11, data.church.logo.indexOf(';'));
        doc.addImage(data.church.logo, fmtType.toUpperCase(), margin, y, logoSize, logoSize);
        textLeft = margin + logoSize + 14;
      } catch (e) { /* skip */ }
    }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(20); doc.setTextColor(11, 37, 69);
    doc.text(data.church?.name || 'Church', textLeft, y + 20);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(90);
    const subInfo = [data.church?.address, data.church?.phone, data.church?.email].filter(Boolean).join('  ·  ');
    if (subInfo) doc.text(subInfo, textLeft, y + 36);
    y += Math.max(logoSize, 42) + 8;
    doc.setDrawColor(11, 37, 69); doc.setLineWidth(1.2);
    doc.line(margin, y, pageW - margin, y);
    doc.setLineWidth(0.4); doc.line(margin, y + 3, pageW - margin, y + 3);
    y += 20;

    // Title
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(11, 37, 69);
    const title = 'TRIAL BALANCE';
    const titleW = doc.getTextWidth(title);
    const tx = (pageW - titleW) / 2;
    doc.setLineWidth(0.8);
    doc.line(tx - 18, y - 4, tx + titleW + 18, y - 4);
    doc.text(title, pageW / 2, y + 12, { align: 'center' });
    doc.line(tx - 18, y + 18, tx + titleW + 18, y + 18);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(90);
    doc.text(`As at ${fmtDateLong(data.period.end_date)}`, pageW / 2, y + 34, { align: 'center' });
    doc.setFontSize(9); doc.setTextColor(120);
    doc.text(`Activity period: ${fmtDateLong(data.period.start_date)} — ${fmtDateLong(data.period.end_date)}`, pageW / 2, y + 48, { align: 'center' });
    y += 60;

    // Table
    const body = rows.map(r => {
      if (r.type === 'section') {
        return [{ content: r.label, colSpan: 3, styles: { fillColor: [11, 37, 69], textColor: 255, fontStyle: 'bold' } }];
      }
      if (r.type === 'note') {
        return [
          { content: r.name, colSpan: 3, styles: { fontStyle: 'italic', textColor: 120, halign: 'center' } },
        ];
      }
      if (r.type === 'subtotal') {
        return [
          { content: r.name, styles: { fontStyle: 'bold', fillColor: [238, 242, 247], textColor: [11, 37, 69] } },
          { content: r.debit  ? fmt(r.debit)  : '', styles: { halign: 'right', fontStyle: 'bold', fillColor: [238, 242, 247], textColor: [11, 37, 69] } },
          { content: r.credit ? fmt(r.credit) : '', styles: { halign: 'right', fontStyle: 'bold', fillColor: [238, 242, 247], textColor: [11, 37, 69] } },
        ];
      }
      return [
        `   ${r.name}`,
        { content: r.debit  ? fmt(r.debit)  : '', styles: { halign: 'right' } },
        { content: r.credit ? fmt(r.credit) : '', styles: { halign: 'right' } },
      ];
    });

    const totalColor = data.totals.balanced ? [15, 118, 110] : [185, 28, 28];
    body.push([
      { content: 'TOTAL', styles: { fontStyle: 'bold', fillColor: [255, 253, 245], textColor: totalColor, fontSize: 12 } },
      { content: fmt(data.totals.debit),  styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 253, 245], textColor: totalColor, fontSize: 12 } },
      { content: fmt(data.totals.credit), styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 253, 245], textColor: totalColor, fontSize: 12 } },
    ]);

    autoTable(doc, {
      startY: y,
      head: [[
        'Account',
        { content: 'Debit (GH₵)',  styles: { halign: 'right' } },
        { content: 'Credit (GH₵)', styles: { halign: 'right' } },
      ]],
      body,
      theme: 'grid',
      styles: { fontSize: 9.5, cellPadding: 5, lineColor: [230, 232, 238], lineWidth: 0.4 },
      headStyles: { fillColor: [11, 37, 69], textColor: 255, fontStyle: 'bold', halign: 'left', valign: 'middle' },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 120, halign: 'right' },
        2: { cellWidth: 120, halign: 'right' },
      },
      margin: { top: 40, left: margin, right: margin, bottom: 80 },
      didDrawPage: () => {
        doc.setFontSize(9); doc.setTextColor(120);
        doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageW - margin, pageH - 24, { align: 'right' });
        doc.text(`Generated ${new Date().toLocaleString('en-GH')}`, margin, pageH - 24);
      },
    });

    // Balance check note
    let by = doc.lastAutoTable?.finalY || y;
    by += 20;
    if (by > pageH - 60) { doc.addPage(); by = 60; }
    if (data.totals.balanced) {
      doc.setDrawColor(15, 118, 110); doc.setFillColor(231, 245, 239);
    } else {
      doc.setDrawColor(185, 28, 28); doc.setFillColor(253, 236, 236);
    }
    doc.setLineWidth(0.6);
    doc.roundedRect(margin, by, pageW - margin * 2, 26, 4, 4, 'FD');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
    doc.setTextColor(...(data.totals.balanced ? [15, 118, 110] : [185, 28, 28]));
    const msg = data.totals.balanced
      ? 'The trial balance is balanced. Debits equal credits.'
      : `The trial balance is out of balance by GH₵ ${fmt(Math.abs(data.totals.difference))}.`;
    doc.text(msg, pageW / 2, by + 17, { align: 'center' });

    const stamp = `${data.period.start_date}_to_${data.period.end_date}`;
    doc.save(`Trial_Balance_${stamp.replace(/[\/\s]/g, '_')}.pdf`);
  };

  // ─── EXPORT EXCEL ─────────────────────────────────────────────────────────
  const handleExcel = () => {
    if (!data) return;
    const aoa = [];
    aoa.push([data.church?.name || 'Church']);
    if (data.church?.address) aoa.push([data.church.address]);
    const contact = [data.church?.phone, data.church?.email].filter(Boolean).join(' · ');
    if (contact) aoa.push([contact]);
    aoa.push([]);
    aoa.push(['TRIAL BALANCE']);
    aoa.push([`As at ${fmtDateLong(data.period.end_date)}`]);
    aoa.push([`Activity period: ${fmtDateLong(data.period.start_date)} — ${fmtDateLong(data.period.end_date)}`]);
    aoa.push([]);
    aoa.push(['Account', 'Debit (GH₵)', 'Credit (GH₵)']);

    rows.forEach(r => {
      if (r.type === 'section') { aoa.push([r.label, '', '']); return; }
      if (r.type === 'note')    { aoa.push([r.name, '', '']); return; }
      if (r.type === 'subtotal') {
        aoa.push([r.name, r.debit  ? Number(r.debit)  : '', r.credit ? Number(r.credit) : '']);
        return;
      }
      aoa.push([`   ${r.name}`, r.debit ? Number(r.debit) : '', r.credit ? Number(r.credit) : '']);
    });
    aoa.push(['TOTAL', Number(data.totals.debit), Number(data.totals.credit)]);
    aoa.push([]);
    aoa.push([data.totals.balanced
      ? 'Balanced — Debits equal Credits.'
      : `OUT OF BALANCE by GH₵ ${fmt(Math.abs(data.totals.difference))}`]);

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 50 }, { wch: 18 }, { wch: 18 }];
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = 1; C <= 2; C++) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = ws[addr];
        if (cell && typeof cell.v === 'number') cell.z = '#,##0.00';
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Trial Balance');
    const stamp = `${data.period.start_date}_to_${data.period.end_date}`;
    XLSX.writeFile(wb, `Trial_Balance_${stamp.replace(/[\/\s]/g, '_')}.xlsx`);
  };

  if (loading) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Building trial balance for {fmtDateLong(start)} — {fmtDateLong(end)}…
        </Typography>
      </Box>
    );
  }
  if (error) return <Alert severity="error" onClose={() => setError('')}>{error}</Alert>;
  if (!data) return null;

  const balanced = data.totals.balanced;

  return (
    <Box>
      {/* Action bar */}
      <Stack direction="row" spacing={1} justifyContent="flex-end" mb={2} flexWrap="wrap">
        <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
        <Button size="small" variant="outlined" startIcon={<Print />} onClick={handlePrint}>Print</Button>
        <Button size="small" variant="outlined" startIcon={<GridOn />} onClick={handleExcel}
          sx={{ color: '#0F6C3D', borderColor: '#0F6C3D', '&:hover': { borderColor: '#0F6C3D', bgcolor: '#0F6C3D0d' } }}>
          Export Excel
        </Button>
        <Button size="small" variant="contained" startIcon={<PictureAsPdf />} onClick={handlePdf}
          sx={{ bgcolor: RED, '&:hover': { bgcolor: '#991B1B' } }}>
          Export PDF
        </Button>
      </Stack>

      {/* Statement paper */}
      <Paper
        elevation={2}
        sx={{
          border: '1px solid #E2E8F0', borderRadius: 2,
          p: { xs: 3, md: 6 }, bgcolor: '#fff',
          maxWidth: 1040, mx: 'auto',
          boxShadow: '0 10px 30px rgba(15,23,42,0.06)',
        }}
      >
        <Box ref={printRef}>
          {/* Letterhead */}
          <Box className="letterhead" sx={{
            display: 'flex', alignItems: 'center', gap: 3,
            pb: 2, borderBottom: `3px double ${RULE}`,
          }}>
            {data.church?.logo ? (
              <Box component="img" src={data.church.logo} alt="Logo"
                sx={{ width: 90, height: 90, objectFit: 'contain', borderRadius: 1 }} />
            ) : (
              <Box sx={{
                width: 90, height: 90, borderRadius: 1,
                bgcolor: `${NAVY}12`, color: NAVY,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 30,
              }}>
                {(data.church?.name || 'C').charAt(0).toUpperCase()}
              </Box>
            )}
            <Box className="info" sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h4" fontWeight={800}
                sx={{ color: NAVY, letterSpacing: '0.02em', lineHeight: 1.1 }}>
                {data.church?.name || 'Church'}
              </Typography>
              {(data.church?.address || data.church?.phone || data.church?.email) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {[data.church?.address, data.church?.phone, data.church?.email].filter(Boolean).join('  ·  ')}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Title block */}
          <Box className="stmt-title" sx={{ textAlign: 'center', mt: 3.5, mb: 0.5 }}>
            <Typography className="name" sx={{
              fontSize: 15, fontWeight: 800, letterSpacing: '0.16em', color: NAVY,
              borderTop: `1.5px solid ${NAVY}`, borderBottom: `1.5px solid ${NAVY}`,
              display: 'inline-block', px: 3, py: 0.5,
            }}>
              TRIAL BALANCE
            </Typography>
            <Typography className="period" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              As at <b>{fmtDateLong(data.period.end_date)}</b>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Activity period: {fmtDateLong(data.period.start_date)} — {fmtDateLong(data.period.end_date)}
            </Typography>
          </Box>

          {/* Table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 18 }}>
            <thead>
              <tr>
                <th style={thStyle}>Account</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Debit (GH₵)</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Credit (GH₵)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                if (r.type === 'section') {
                  return (
                    <tr key={`sec-${i}`}>
                      <td colSpan={3} style={sectionHeadStyle}>{r.label}</td>
                    </tr>
                  );
                }
                if (r.type === 'note') {
                  return (
                    <tr key={`note-${i}`}>
                      <td colSpan={3} style={noteStyle}>{r.name}</td>
                    </tr>
                  );
                }
                if (r.type === 'subtotal') {
                  return (
                    <tr key={`sub-${i}`}>
                      <td style={subtotalLabelStyle}>{r.name}</td>
                      <td style={subtotalAmountStyle}>{r.debit  ? fmt(r.debit)  : ''}</td>
                      <td style={subtotalAmountStyle}>{r.credit ? fmt(r.credit) : ''}</td>
                    </tr>
                  );
                }
                return (
                  <tr key={`row-${i}`}>
                    <td style={itemStyle}>{r.name}</td>
                    <td style={amountStyle}>{r.debit  ? fmt(r.debit)  : ''}</td>
                    <td style={amountStyle}>{r.credit ? fmt(r.credit) : ''}</td>
                  </tr>
                );
              })}
              <tr>
                <td style={grandLabelStyle(balanced)}>TOTAL</td>
                <td style={grandAmountStyle(balanced)}>{fmt(data.totals.debit)}</td>
                <td style={grandAmountStyle(balanced)}>{fmt(data.totals.credit)}</td>
              </tr>
            </tbody>
          </table>

          {/* Balance check */}
          <Box sx={{
            mt: 2.5, p: 1.5, borderRadius: 1.5, textAlign: 'center',
            border: `1px solid ${balanced ? GREEN : RED}`,
            bgcolor: balanced ? '#e7f5ef' : '#fdecec',
            color:   balanced ? GREEN : RED,
            fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
          }}>
            {balanced ? <CheckCircle sx={{ fontSize: 20 }} /> : <ErrorOutline sx={{ fontSize: 20 }} />}
            <Typography component="span" fontWeight={700}>
              {balanced
                ? 'The trial balance is balanced. Debits equal credits.'
                : `Out of balance by GH₵ ${fmt(Math.abs(data.totals.difference))}`}
            </Typography>
          </Box>

          {/* Footer */}
          <Box className="footer" sx={{
            display: 'flex', justifyContent: 'space-between', mt: 4,
            fontSize: 10, color: '#888',
          }}>
            <span>Generated {new Date().toLocaleString('en-GH')}</span>
            <span>{data.church?.name || ''}</span>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

// ─── Inline styles ─────────────────────────────────────────────────────────
const thStyle = {
  textAlign: 'left', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
  color: '#fff', background: NAVY, padding: '10px 12px', fontWeight: 700,
};
const itemStyle    = { padding: '6px 12px', fontSize: 13.5, borderBottom: '1px solid #eef0f4', paddingLeft: 28, color: '#1e293b' };
const amountStyle  = { padding: '6px 12px', fontSize: 13.5, borderBottom: '1px solid #eef0f4',
                       textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                       fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#1e293b' };
const sectionHeadStyle = {
  background: NAVY, color: '#fff', fontWeight: 800, letterSpacing: '0.1em',
  padding: '9px 12px', fontSize: 12, textTransform: 'uppercase',
};
const noteStyle = {
  padding: '10px 12px', fontStyle: 'italic', color: '#999',
  fontSize: 12.5, textAlign: 'center',
};
const subtotalLabelStyle = {
  padding: '9px 12px', fontSize: 13, background: '#eef2f7', color: NAVY, fontWeight: 800,
  borderTop: `1px solid ${NAVY}`, borderBottom: `1px solid ${NAVY}`, textTransform: 'uppercase',
};
const subtotalAmountStyle = {
  padding: '9px 12px', fontSize: 13, background: '#eef2f7', color: NAVY, fontWeight: 800,
  textAlign: 'right', borderTop: `1px solid ${NAVY}`, borderBottom: `1px solid ${NAVY}`,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};
const grandLabelStyle = (balanced) => ({
  padding: '14px 12px', fontSize: 15, fontWeight: 800, background: '#fffdf5',
  color: balanced ? GREEN : RED,
  borderTop: `3px double ${NAVY}`, borderBottom: `3px double ${NAVY}`,
  textTransform: 'uppercase', letterSpacing: '0.1em',
});
const grandAmountStyle = (balanced) => ({
  padding: '14px 12px', fontSize: 15, fontWeight: 800, textAlign: 'right', background: '#fffdf5',
  color: balanced ? GREEN : RED,
  borderTop: `3px double ${NAVY}`, borderBottom: `3px double ${NAVY}`,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
});
